import { ref, type Ref } from 'vue'

const EDGE_RESIZE_STEP = 16
const EDGE_RESIZE_STEP_LARGE = 64
const EDGE_RESIZE_DRAG_THRESHOLD = 3

export interface EdgeResizeBounds {
  min: number
  max: number
}

export interface EdgeResizeOptions {
  /** Which edge of the resized panel the handle sits on; dragging away from the panel grows it. */
  edge: 'left' | 'right'
  /** The panel's current width in px. */
  getWidth: () => number
  /** The width limits in px. */
  getBounds: () => EdgeResizeBounds
  /** Applies a width: `persist` is false while the drag is live and true when it settles. */
  setWidth: (px: number, persist: boolean) => void
  /** Runs when the handle is clicked without a drag. */
  onClick?: () => void
  /** Runs when Enter is pressed on the handle. */
  onActivate?: () => void
  /** Runs when the handle is double-clicked. */
  onReset?: () => void
}

export interface EdgeResize {
  dragging: Ref<boolean>
  onPointerdown: (event: PointerEvent) => void
  onPointermove: (event: PointerEvent) => void
  onPointerup: (event: PointerEvent) => void
  onPointercancel: (event: PointerEvent) => void
  onKeydown: (event: KeyboardEvent) => void
  onDblclick: () => void
}

/** Clamps `value` to `[min, max]`. */
export function clampWidth(value: number, bounds: EdgeResizeBounds): number {
  return Math.min(bounds.max, Math.max(bounds.min, value))
}

/** Whether a pointer movement of `delta` px is large enough to count as a drag rather than a click. */
export function exceedsDragThreshold(delta: number): boolean {
  return Math.abs(delta) > EDGE_RESIZE_DRAG_THRESHOLD
}

/** The width a drag asks for: the start width plus the pointer travel, signed so that moving away from the panel grows it. */
export function dragWidth(
  edge: 'left' | 'right',
  startWidth: number,
  startX: number,
  clientX: number,
): number {
  return edge === 'right' ? startWidth + (clientX - startX) : startWidth + (startX - clientX)
}

/** The width a key asks for, or `null` when the key is not a resize key. Arrows move by a step (a large step with Shift) and Home and End jump to the limits. */
export function keyboardWidth(
  edge: 'left' | 'right',
  key: string,
  shift: boolean,
  width: number,
  bounds: EdgeResizeBounds,
): number | null {
  const step = shift ? EDGE_RESIZE_STEP_LARGE : EDGE_RESIZE_STEP
  const growKey = edge === 'right' ? 'ArrowRight' : 'ArrowLeft'
  const shrinkKey = edge === 'right' ? 'ArrowLeft' : 'ArrowRight'
  if (key === growKey) return clampWidth(width + step, bounds)
  if (key === shrinkKey) return clampWidth(width - step, bounds)
  if (key === 'Home') return bounds.min
  if (key === 'End') return bounds.max
  return null
}

/** Resolves CSS length variables to px through a hidden probe, since `getComputedStyle` cannot resolve `min()` or `clamp()` from raw text. Returns zeros outside a browser. */
export function measureCssLengths(names: readonly string[]): number[] {
  if (typeof document === 'undefined') return names.map(() => 0)
  const probe = document.createElement('div')
  probe.style.cssText =
    'position:fixed;top:-9999px;left:-9999px;visibility:hidden;pointer-events:none;'
  document.body.appendChild(probe)
  const widths = names.map((name) => {
    probe.style.width = `var(${name})`
    return probe.getBoundingClientRect().width
  })
  probe.remove()
  return widths
}

/** Pointer-drag and keyboard logic for a vertical resize edge; bind the returned handlers to a separator element. */
export function useEdgeResize(options: EdgeResizeOptions): EdgeResize {
  const dragging = ref(false)
  let pressed = false
  let startX = 0
  let startWidth = 0

  function onPointerdown(event: PointerEvent) {
    pressed = true
    dragging.value = false
    startX = event.clientX
    startWidth = options.getWidth()
    ;(event.currentTarget as HTMLElement).setPointerCapture(event.pointerId)
  }

  function onPointermove(event: PointerEvent) {
    if (!pressed) return
    if (!dragging.value) {
      if (!exceedsDragThreshold(event.clientX - startX)) return
      dragging.value = true
    }
    options.setWidth(dragWidth(options.edge, startWidth, startX, event.clientX), false)
  }

  function finish(event: PointerEvent, cancelled: boolean) {
    if (!pressed) return
    pressed = false
    const target = event.currentTarget as HTMLElement
    if (target.hasPointerCapture(event.pointerId)) target.releasePointerCapture(event.pointerId)
    const wasDragging = dragging.value
    dragging.value = false
    if (wasDragging) options.setWidth(options.getWidth(), true)
    else if (!cancelled) options.onClick?.()
  }

  function onKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      if (!options.onActivate) return
      event.preventDefault()
      options.onActivate()
      return
    }
    const next = keyboardWidth(
      options.edge,
      event.key,
      event.shiftKey,
      options.getWidth(),
      options.getBounds(),
    )
    if (next === null) return
    event.preventDefault()
    options.setWidth(next, true)
  }

  return {
    dragging,
    onPointerdown,
    onPointermove,
    onPointerup: (event) => finish(event, false),
    onPointercancel: (event) => finish(event, true),
    onKeydown,
    onDblclick: () => options.onReset?.(),
  }
}
